import * as Yup from "yup";
import { EMAIL_REGEX, MOBILE_REGEX, NOSPACE_REGEX } from "./constants";
import { MESSAGE } from "./validationMessage";
import dayjs from "dayjs";
import isSameOrAfter from "dayjs/plugin/isSameOrAfter";
import isSameOrBefore from "dayjs/plugin/isSameOrBefore";

const stripHtml = (html) => {
  const tmp = document.createElement("div");
  tmp.innerHTML = html;
  return tmp.textContent || tmp.innerText || "";
};

// Extend dayjs with plugins
dayjs.extend(isSameOrAfter);
dayjs.extend(isSameOrBefore);

export let email = Yup.string()
  .max(254, "Email should not be longer than 254 characters")
  .required(MESSAGE.REQUIRED)
  .email(MESSAGE.EMAIL)
  .matches(EMAIL_REGEX, MESSAGE.EMAIL);

const editorContentValidation = (cmsData, context) => {
  if (cmsData === undefined || cmsData === null) {
    return context.createError({ message: "Description is required" });
  }

  const plainText = cmsData
    .replace(/<\/?[^>]*>/g, "")
    .replace(/&nbsp;/g, " ")
    .replace(/<br\s*[/]?>/gi, "\n")
    .trim();

  if (!plainText) {
    return context.createError({ message: "Description is required" });
  }

  // if (/^\s|^(&nbsp;)+/i.test(cmsData.replace(/<[^>]*>/g, ""))) {
  //   return context.createError({ message: "Description should not start with a space" });
  // }

  if (plainText.length < 10) {
    return context.createError({
      message: "Description must be at least 10 characters long",
    });
  }

  return true;
};

export const AddUserSchema = Yup.object()
  .shape({
    email: Yup.string()
      .trim()
      .max(254, "Email should not be longer than 254 characters")
      .email("Invalid email format")
      .test(
        "has-at-and-com",
        "Email must contain '@' and end with '.com'",
        (value) => {
          if (!value) return true; // ✅ allow empty email
          return value.includes("@") && value.toLowerCase();
        },
      ),
    profilePhoto: Yup.mixed()
      .required("Profile photo is required")
      .test("fileType", "Only JPG, JPEG, PNG allowed", (file) => {
        if (!file) return false;
        return ["image/jpeg", "image/jpg", "image/png"].includes(file.type);
      })
      .test("fileSize", "Profile photo cannot exceed 20MB", (file) => {
        if (!file) return false;
        return file.size <= 20 * 1024 * 1024;
      }),
    videoIntroduction: Yup.mixed()
      .notRequired()

      // Remove strict validation since you're using toast
      .test("is-valid-video", "Please select a valid video file", (value) => {
        if (!value) return true; // Allow empty
        return true; // Validation happens in validateVideoFile function
      })
      .test("fileSize", "Video cannot exceed 50MB", (file) => {
        if (!file) return true;
        return file.size <= 50 * 1024 * 1024;
      }),

    verificationDocs: Yup.array()
      .of(
        Yup.mixed()
          .test("is-valid-pdf", "Please select valid PDF files", (file) => {
            if (!file) return true;
            return true; // Validation happens in validatePdfFile function
          })
          .test(
            "fileSize",
            "Each document cannot exceed 20MB",
            (file) => !file || file.size <= 20 * 1024 * 1024,
          ),
      )
      .nullable()
      .transform((value) => {
        if (!value || value.length === 0) return null;
        return value;
      })
      .test("verificationDocs-validation", function (value) {
        if (value === null || value === undefined || value.length === 0)
          return true; // Optional field
        if (value.length < 1) {
          return this.createError({
            message: "At least one verification document is required",
          });
        }
        if (value.length > 3) {
          return this.createError({
            message: "You can select at most 3 verification document",
          });
        }
        return true;
      }),
    additionalPhotos: Yup.array()
      .of(
        Yup.mixed()
          .test("is-valid-image", "Please select valid image files", (file) => {
            if (!file) return true;
            return true; // Validation happens in validateImageFile function
          })
          .test(
            "fileSize",
            "Each photo cannot exceed 20MB",
            (file) => !file || file.size <= 20 * 1024 * 1024,
          ),
      )
      .nullable()
      .transform((value) => {
        if (!value || value.length === 0) return null;
        return value;
      })
      .test("additionalPhotos-validation", function (value) {
        if (value === null || value === undefined || value.length === 0)
          return true; // Optional field
        if (value.length < 1) {
          return this.createError({
            message: "At least one additional photo is required",
          });
        }
        if (value.length > 9) {
          return this.createError({ message: "Maximum 9 photos allowed" });
        }
        return true;
      }),

    password: Yup.string()
      .required("Password is required")
      .min(6, "Password must be at least 6 characters")
      .max(12, "Password cannot be more than 12 characters")
      .matches(
        /^(?=.*[A-Z])(?=.*\d)(?=.*[!@#$%^&*()_+\-=\[\]{};':"\\|,.<>\/?]).{6,12}$/,
        "Password must include at least one uppercase letter, one number, and one special character",
      ),

    fullName: Yup.string()
      .required("Full name is required")
      .test(
        "not-empty",
        "Full name cannot be only spaces",
        (value) => value && value.trim().length > 0,
      )
      .max(15, "Full name cannot be longer than 15 characters")
      .matches(/^[A-Za-z\s]+$/, "Full name can only contain letters and spaces")
      .test(
        "no-leading-trailing-spaces",
        "Full Name cannot have leading or trailing spaces",
        (value) => value === value?.trim(),
      ),

    countrycode: Yup.string() // Changed from countryCode
      .required("Country code is required")
      .matches(/^\+\d+$/, "Invalid country code format"),
    genderprefrences: Yup.string().required("Gender preference is required"),

    phoneNumber: Yup.string()
      .required("Phone number is required")
      .matches(/^\d+$/, "Phone must contain only numbers")
      .min(8, "Phone number is too short")
      .max(15, "Phone number cannot exceed 15 digits"),

    birthday: Yup.date()
      .required("Birthday is required")
      .typeError("Please select a valid date")
      .max(
        dayjs().subtract(18, "year").toDate(),
        "You must be at least 18 years old",
      )
      .min(
        dayjs().subtract(100, "year").toDate(),
        "Age cannot exceed 100 years",
      )
      .test("is-valid-birthday", "Please select a valid birthday", (value) => {
        if (!value) return false;
        const age = dayjs().diff(dayjs(value), "year");
        return age >= 18 && age <= 100;
      }),

    credits: Yup.number()
      .required("Credits is required")
      .typeError("Credits must be a number")
      .min(0, "Credits cannot be less than 0"),

    bio: Yup.string()
      .required("Bio is required")
      .min(10, "Bio must be 10 characters")
      .max(50, "Bio must not exceed 50 characters")
      .test(
        "not-empty",
        "Full name cannot be only spaces",
        (value) => value && value.trim().length > 0,
      )
      .test(
        "no-leading-trailing-spaces",
        "Bio cannot have leading or trailing spaces",
        (value) => value === value?.trim(),
      ),

    lookingFor: Yup.string().oneOf(
      ["dating", "playdate", "both"],
      "Invalid option for looking for",
    ),

    subscription: Yup.string()
      .nullable()
      .test(
        "valid-plan",
        "Invalid subscription plan selected",
        function (value) {
          if (!value) return true; // Empty is allowed (free plan)

          // Check if the value is a valid ObjectId format
          const objectIdPattern = /^[0-9a-fA-F]{24}$/;
          if (!objectIdPattern.test(value)) {
            return false;
          }

          return true;
        },
      ),

    interests: Yup.array()
      .of(Yup.string().trim())
      .min(1, "At least one interest is required")
      .max(6, "You can select at most 6 interests options")
      .required("Interests are required"),

    petCompatibilityPreferences: Yup.array()
      .of(Yup.string().trim())
      .min(1, "Select at least one preference")
      .max(6, "You can select at most 6 compatibility options")
      .required("Pet compatibility preferences are required"),

    // availability: Yup.array()
    //   .of(Yup.string().trim())
    //   .min(1, "Select at least one availability option")
    //   .max(6, "You can select at most 6 availability options")
    //   .required("Availability is required"),
    city: Yup.string()
      .trim()
      .max(50, "City name too long")
      .matches(
        /^[A-Za-z]+(?:[\s-][A-Za-z]+)*$/,
        "City name must contain only alphabets",
      )
      .required("city is required"),

    state: Yup.string()
      .trim()
      .max(50, "State name too long")
      .matches(
        /^[A-Za-z]+(?:[\s-][A-Za-z]+)*$/,
        "State name must contain only alphabets",
      )
      .required("state is required"),

    country: Yup.string()
      .trim()
      .max(50, "Country name too long")
      .matches(
        /^[A-Za-z]+(?:[\s-][A-Za-z]+)*$/,
        "Country name must contain only alphabets",
      )
      .required("country is required"),
    // latitude: Yup.number().typeError("Must be a number"),
    // longitude: Yup.number().typeError("Must be a number"),
    latitude: Yup.number()
      .typeError("Latitude must be a valid number")
      .required("Latitude is required")
      .test(
        "is-valid-latitude",
        "Latitude must be between -90 and 90",
        (value) => Number.isFinite(value) && Math.abs(value) <= 90,
      ),

    longitude: Yup.number()
      .typeError("Longitude must be a valid number")
      .required("Longitude is required")
      .test(
        "is-valid-longitude",
        "Longitude must be between -180 and 180",
        (value) => Number.isFinite(value) && Math.abs(value) <= 180,
      ),

    discoveryRadiusMin: Yup.number()
      .typeError("Discovery radius (min) must be a number")
      .nullable()
      .transform((value, originalValue) => {
        if (
          originalValue === "" ||
          originalValue === null ||
          originalValue === undefined
        )
          return null;
        return value;
      })
      .test(
        "discoveryRadiusMin-validation",
        "Minimum discovery radius cannot be less than 1 and cannot be more than 200",
        function (value) {
          if (value === null || value === undefined || value === "")
            return true; // Optional field
          return value >= 1 && value <= 200;
        },
      ),

    discoveryRadiusMax: Yup.number()
      .typeError("Discovery radius (max) must be a number")
      .nullable()
      .transform((value, originalValue) => {
        if (
          originalValue === "" ||
          originalValue === null ||
          originalValue === undefined
        )
          return null;
        return value;
      })
      .test(
        "discoveryRadiusMax-validation",
        "Maximum discovery radius cannot be less than 1 and cannot be more than 200",
        function (value) {
          if (value === null || value === undefined || value === "")
            return true; // Optional field
          return value >= 1 && value <= 200;
        },
      ),

    agePrefMin: Yup.number()
      .typeError("Minimum age preference must be a number")
      .nullable()
      .transform((value, originalValue) => {
        if (
          originalValue === "" ||
          originalValue === null ||
          originalValue === undefined
        )
          return null;
        return value;
      })
      .test(
        "agePrefMin-validation",
        "Minimum age cannot be less than 1 and cannot be more than 100",
        function (value) {
          if (value === null || value === undefined || value === "")
            return true; // Optional field
          return value >= 1 && value <= 100;
        },
      ),

    agePrefMax: Yup.number()
      .typeError("Maximum age preference must be a number")
      .nullable()
      .transform((value, originalValue) => {
        if (
          originalValue === "" ||
          originalValue === null ||
          originalValue === undefined
        )
          return null;
        return value;
      })
      .test(
        "agePrefMax-validation",
        "Maximum age cannot be less than 1 and cannot be more than 100",
        function (value) {
          if (value === null || value === undefined || value === "")
            return true; // Optional field
          return value >= 1 && value <= 100;
        },
      ),

    gender: Yup.string().required("Gender is required"),

    socialMediaLinks: Yup.object({
      instagram: Yup.string().url("Enter a valid Instagram URL").nullable(),
      facebook: Yup.string().url("Enter a valid Facebook URL").nullable(),
      tiktok: Yup.string().url("Enter a valid TikTok URL").nullable(),
    }).nullable(),
  })
  .test(
    "email-or-phone",
    "Either email or phone is required",
    function (values) {
      const { email, phoneNumber } = values;
      if (!email && !phoneNumber) {
        return this.createError({
          path: "email",
          message: "Either Email or Phone is required",
        });
      }
      return true;
    },
  );
export const EditUserSchema = Yup.object().shape({
  email: Yup.string()
    .trim()
    .matches(
      /^[a-zA-Z0-9._%+-]+@[a-zA-Z0-9.-]+\.[A-Za-z]{2,6}$/,
      "Enter a valid email address",
    )
    .nullable(),

  fullName: Yup.string()

    .max(15, "Full Name max length is 15")
    .matches(
      /^[A-Za-z\s]+$/,
      "Full Name cannot contain numbers or special characters",
    )
    .test(
      "not-empty",
      "Full name cannot be only spaces",
      (value) => value && value.trim().length > 0,
    )
    .test(
      "no-leading-trailing-spaces",
      "Full Name cannot have leading or trailing spaces",
      (value) => value === value?.trim(),
    ),

  countryCode: Yup.string().matches(/^\+\d+$/, "Invalid country code format"),

  phoneNumber: Yup.string()
    .matches(/^\d+$/, "Phone must contain only numbers")
    .min(8, "Phone number is too short")
    .max(15, "Phone number cannot exceed 15 digits"),

  birthday: Yup.date()
    .required("Birthday is required")
    .typeError("Please select a valid date")
    .max(
      dayjs().subtract(18, "year").toDate(),
      "You must be at least 18 years old",
    )
    .min(dayjs().subtract(100, "year").toDate(), "Age cannot exceed 100 years")
    .test("is-valid-birthday", "Please select a valid birthday", (value) => {
      if (!value) return false;
      const age = dayjs().diff(dayjs(value), "year");
      return age >= 18 && age <= 100;
    }),
  gender: Yup.string().required("Gender is required"),

  bio: Yup.string()
    .min(10, "Bio must be 10 characters")
    .max(50, "Bio must not exceed 50 characters")
    .test(
      "not-empty",
      "Bio cannot be only spaces",
      (value) => value && value.trim().length > 0,
    )
    .test(
      "no-leading-trailing-spaces",
      "Bio cannot have leading or trailing spaces",
      (value) => value === value?.trim(),
    ),

  credits: Yup.number()
    .typeError("Credits must be a number")
    .min(0, "Credits cannot be less than 0"),

  subscription: Yup.string()
    .nullable()
    .test("valid-plan", "Invalid subscription plan", function (value) {
      if (!value) return true; // Empty is allowed (free plan)
      // Check if it's a valid MongoDB ObjectId
      const objectIdPattern = /^[0-9a-fA-F]{24}$/;
      return objectIdPattern.test(value);
    }),
  lookingFor: Yup.string().oneOf(
    ["dating", "playdate", "both"],
    "Invalid option",
  ),

  city: Yup.string()
    .trim()
    .max(50, "City name too long")
    .matches(
      /^[A-Za-z]+(?:[\s-][A-Za-z]+)*$/,
      "City name must contain only alphabets",
    )
    .required("city is required"),

  state: Yup.string()
    .trim()
    .max(50, "State name too long")
    .matches(
      /^[A-Za-z]+(?:[\s-][A-Za-z]+)*$/,
      "State name must contain only alphabets",
    )
    .required("state is required"),

  country: Yup.string()
    .trim()
    .max(50, "Country name too long")
    .matches(
      /^[A-Za-z]+(?:[\s-][A-Za-z]+)*$/,
      "Country name must contain only alphabets",
    )
    .required("country is required"),
  // latitude: Yup.number().typeError("Must be a number"),
  // longitude: Yup.number().typeError("Must be a number"),
  latitude: Yup.number()
    .typeError("Latitude must be a number")
    .min(-90, "Latitude must be between -90 and 90")
    .max(90, "Latitude must be between -90 and 90")
    .required("Latitude is required"),

  longitude: Yup.number()
    .typeError("Longitude must be a number")
    .min(-180, "Longitude must be between -180 and 180")
    .max(180, "Longitude must be between -180 and 180")
    .required("Longitude is required"),

  // Preferences
  discoveryRadiusMin: Yup.number()
    .typeError("Discovery radius (min) must be a number")
    .nullable()
    .transform((value, originalValue) => {
      if (
        originalValue === "" ||
        originalValue === null ||
        originalValue === undefined
      )
        return null;
      return value;
    })
    .test(
      "discoveryRadiusMin-validation",
      "Minimum discovery radius cannot be less than 1 and cannot be more than 200",
      function (value) {
        if (value === null || value === undefined || value === "") return true; // Optional field
        return value >= 1 && value <= 200;
      },
    ),

  discoveryRadiusMax: Yup.number()
    .typeError("Discovery radius (max) must be a number")
    .nullable()
    .transform((value, originalValue) => {
      if (
        originalValue === "" ||
        originalValue === null ||
        originalValue === undefined
      )
        return null;
      return value;
    })
    .test(
      "discoveryRadiusMax-validation",
      "Maximum discovery radius cannot be less than 1 and cannot be more than 200",
      function (value) {
        if (value === null || value === undefined || value === "") return true; // Optional field
        return value >= 1 && value <= 200;
      },
    ),

  agePrefMin: Yup.number()
    .typeError("Minimum age preference must be a number")
    .nullable()
    .transform((value, originalValue) => {
      if (
        originalValue === "" ||
        originalValue === null ||
        originalValue === undefined
      )
        return null;
      return value;
    })
    .test(
      "agePrefMin-validation",
      "Minimum age cannot be less than 1 and cannot be more than 100",
      function (value) {
        if (value === null || value === undefined || value === "") return true; // Optional field
        return value >= 1 && value <= 100;
      },
    ),

  agePrefMax: Yup.number()
    .typeError("Maximum age preference must be a number")
    .nullable()
    .transform((value, originalValue) => {
      if (
        originalValue === "" ||
        originalValue === null ||
        originalValue === undefined
      )
        return null;
      return value;
    })
    .test(
      "agePrefMax-validation",
      "Maximum age cannot be less than 1 and cannot be more than 100",
      function (value) {
        if (value === null || value === undefined || value === "") return true; // Optional field
        return value >= 1 && value <= 100;
      },
    ),

  interests: Yup.array()
    .of(Yup.string().trim())
    .min(1, "At least one interest is required")
    .max(6, "You can select at most 6 interests options")
    .required("Interests are required"),

  petCompatibilityPreferences: Yup.array()
    .of(Yup.string().trim())
    .min(1, "Select at least one preference")
    .max(6, "You can select at most 6 compatibility options")
    .required("Pet compatibility preferences are required"),

  // availability: Yup.array()
  //   .of(Yup.string().trim())
  //   .min(1, "Select at least one availability option")
  //   .max(6, "You can select at most 6 availability options")
  //   .required("Availability is required"),
  socialMediaLinks: Yup.object({
    instagram: Yup.string().url("Enter a valid Instagram URL").nullable(),
    facebook: Yup.string().url("Enter a valid Facebook URL").nullable(),
    tiktok: Yup.string().url("Enter a valid TikTok URL").nullable(),
  }).nullable(),

  profilePhoto: Yup.mixed()
    .required("Profile photo is required")
    .test("fileType", "Only JPG, JPEG, PNG allowed", (file) => {
      if (!file) return false; // required check
      if (typeof file === "string") return true; // <-- IMPORTANT
      return ["image/jpeg", "image/jpg", "image/png"].includes(file.type);
    })
    .test("fileSize", "Profile photo cannot exceed 20MB", (file) => {
      if (!file) return false;
      if (typeof file === "string") return true; // <-- IMPORTANT
      return file.size <= 20 * 1024 * 1024;
    }),

  videoIntroduction: Yup.mixed()
    .nullable()
    .notRequired()
    .transform((value) => {
      if (value === "" || value === null || value === undefined) return null;
      return value;
    })
    .test("is-valid-video", "Please select a valid video file", (value) => {
      if (!value || value === null) return true; // Allow empty/null
      return true; // Validation happens in validateVideoFile function
    })
    .test("fileSize", "Video cannot exceed 50MB", (file) => {
      if (!file || file === null) return true;
      if (typeof file === "string") return true; // Existing file path
      return file.size <= 50 * 1024 * 1024;
    }),

  verificationDocs: Yup.array()
    .of(
      Yup.mixed()
        .test("is-valid-pdf", "Please select valid PDF files", (file) => {
          if (!file) return true;
          return true; // Validation happens in validatePdfFile function
        })
        .test("fileSize", "Each document cannot exceed 20MB", (file) => {
          if (!file) return true;
          if (typeof file === "string") return true; // skip validation for existing docs
          return file.size <= 20 * 1024 * 1024;
        }),
    )
    .nullable()
    .transform((value) => {
      if (!value || value.length === 0) return null;
      return value;
    })
    .test("verificationDocs-validation", function (value) {
      if (value === null || value === undefined || value.length === 0)
        return true; // Optional field
      if (value.length < 1) {
        return this.createError({
          message: "At least one verification document is required",
        });
      }
      if (value.length > 3) {
        return this.createError({
          message: "You can select at most 3 verification document",
        });
      }
      return true;
    }),

  additionalPhotos: Yup.array()
    .of(
      Yup.mixed()
        .test("is-valid-image", "Please select valid image files", (file) => {
          if (!file) return true;
          return true; // Validation happens in validateImageFile function
        })
        .test("fileSize", "Each photo cannot exceed 20MB", (file) => {
          if (!file) return true;
          if (typeof file === "string") return true;
          return file.size <= 20 * 1024 * 1024;
        }),
    )
    .nullable()
    .transform((value) => {
      if (!value || value.length === 0) return null;
      return value;
    })
    .test("additionalPhotos-validation", function (value) {
      if (value === null || value === undefined || value.length === 0)
        return true; // Optional field
      if (value.length < 1) {
        return this.createError({
          message: "At least one additional photo is required",
        });
      }
      if (value.length > 9) {
        return this.createError({ message: "Maximum 9 photos allowed" });
      }
      return true;
    }),
});

export const AddSubAdminSchema = Yup.object().shape({
  name: Yup.string()
    .required("Name is required")
    .max(15, "Name max length is 15")
    .matches(/^[A-Za-z\s]+$/, "Name can only contain letters and spaces")
    .test("not-only-spaces", "Name cannot be empty or just spaces", (value) => {
      return value && value.trim().length > 0;
    })
    .test(
      "no-leading-trailing-spaces",
      "Email cannot have leading or trailing spaces",
      (value) => value === value?.trim(),
    ),

  email: Yup.string()
    .required("Email is required")
    .max(254, "Email should not be longer than 254 characters")
    .email("Invalid email format")
    .test(
      "has-at-and-com",
      "Email must contain '@' and end with '.com'",
      (value) => {
        if (!value) return false;
        return value.includes("@") && value.toLowerCase().endsWith(".com");
      },
    )
    .test(
      "no-leading-trailing-spaces",
      "Email cannot have leading or trailing spaces",
      (value) => value === value?.trim(),
    ),

  roleId: Yup.string().required("Role is required"),
});

export const AddRoleSchema = Yup.object().shape({
  name: Yup.string()
    .required("Role name is required")
    .test(
      "not-only-spaces",
      "Role name cannot be empty or just spaces",
      (value) => {
        return value && value.trim().length > 0;
      },
    )
    .test(
      "no-leading-trailing-spaces",
      "Role name cannot have leading or trailing spaces",
      (value) => value === value?.trim(),
    ),

  permissions: Yup.array()
    .min(1, "At least one permission is required")
    .required("Permissions are required"),
});

export const EditOrganizervalidationSchema = Yup.object({
  businessName: Yup.string()
    .required("Business name is required")
    .test(
      "not-empty",
      "Business name cannot be only spaces",
      (value) => value && value.trim().length > 0,
    )
    .matches(
      /^[A-Za-z0-9\s]+$/,
      "Business name can only contain letters, numbers, and spaces",
    )
    .test(
      "no-leading-trailing-spaces",
      "Leading and trailing spaces are not allowed",
      (value) => value === value?.trim(),
    )
    .max(20, "Business name cannot be longer than 20 characters"),

  ownerName: Yup.string()
    .required("Owner name is required")
    .test(
      "not-empty",
      "Owner name cannot be only spaces",
      (value) => value && value.trim().length > 0,
    )
    .matches(/^[A-Za-z\s]+$/, "Owner name can only contain letters and spaces")
    .max(20, "Owner name cannot be longer than 20 characters")
    .test(
      "no-leading-trailing-spaces",
      "Leading and trailing spaces are not allowed",
      (value) => value === value?.trim(),
    ),

  email: Yup.string()
    .email("Enter a valid email")
    .required("Email is required")
    .test(
      "no-leading-trailing-spaces",
      "Leading and trailing spaces are not allowed",
      (value) => value === value?.trim(),
    ),
  phone: Yup.string()
    .required("Phone is required")
    .matches(/^\d+$/, "Phone must contain only numbers")
    .min(8, "Phone number is too short")
    .max(15, "Phone number cannot exceed 15 digits"),
  organizerType: Yup.array()
    .of(Yup.string())
    .min(1, "Please select at least one organizer type")
    .max(3, "You can select a maximum of 3 organizer types")
    .required("Organizer Type is required"),
  businessDescription: Yup.string()
    .required("Business description is required")
    .test(
      "not-empty",
      "Business description cannot be only spaces",
      (value) => value && value.trim().length > 0,
    )
    .min(10, "Business description must be at least 10 characters long")
    .max(100, "Business description cannot exceed 100 characters")
    .test(
      "no-leading-trailing-spaces",
      "Leading and trailing spaces are not allowed",
      (value) => value === value?.trim(),
    ),
  websiteUrl: Yup.string()
    .url("Enter a valid URL")
    .required("website Url is required"),

  country: Yup.string()
    .required("Country is required")
    .matches(/^(?!\s*$).+$/, "Country cannot be empty or only spaces")
    .matches(/^[A-Za-z\s]+$/, "Country can only contain alphabets")
    .max(20, "Country must be at most 20 characters long")
    .test(
      "no-leading-trailing-spaces",
      "Leading and trailing spaces are not allowed",
      (value) => value === value?.trim(),
    ),

  city: Yup.string()
    .required("City is required")
    .matches(/^(?!\s*$).+$/, "City cannot be empty or only spaces")
    .matches(/^[A-Za-z\s]+$/, "City can only contain alphabets")
    .max(20, "City must be at most 20 characters long")
    .test(
      "no-leading-trailing-spaces",
      "Leading and trailing spaces are not allowed",
      (value) => value === value?.trim(),
    ),

  state: Yup.string()
    .required("State is required")
    .matches(/^(?!\s*$).+$/, "State cannot be empty or only spaces")
    .matches(/^[A-Za-z\s]+$/, "State can only contain alphabets")
    .max(20, "State must be at most 20 characters long")
    .test(
      "no-leading-trailing-spaces",
      "Leading and trailing spaces are not allowed",
      (value) => value === value?.trim(),
    ),

  address: Yup.string()
    .required("Address is required")
    .matches(/^(?!\s*$).+$/, "Address cannot be empty or only spaces")
    .matches(
      /^[A-Za-z0-9\s,.-]+$/,
      "Address can only contain letters, numbers, commas, periods, and spaces",
    )
    .max(120, "Address must be at most 120 characters long"),

  facebook: Yup.string().url("Enter a valid URL").nullable().notRequired(),
  instagram: Yup.string().url("Enter a valid URL").nullable().notRequired(),
  linkedin: Yup.string().url("Enter a valid URL").nullable().notRequired(),
  twitter: Yup.string().url("Enter a valid URL").nullable().notRequired(),

  businessLogo: Yup.mixed()
    .nullable()
    .notRequired()
    .test("fileType", "Only JPG, JPEG, or PNG files are allowed", (value) => {
      if (!value || typeof value === "string") return true;
      const allowedTypes = ["image/jpeg", "image/jpg", "image/png"];
      return allowedTypes.includes(value.type);
    }),

  coverPhoto: Yup.mixed()
    .nullable()
    .notRequired()
    .test("fileType", "Only JPG, JPEG, or PNG files are allowed", (value) => {
      if (!value || typeof value === "string") return true;
      const allowedTypes = ["image/jpeg", "image/jpg", "image/png"];
      return allowedTypes.includes(value.type);
    }),

  businessLicense: Yup.mixed()
    .nullable()
    .notRequired()
    .test("fileType", "Only PDF files are allowed", (value) => {
      if (!value || typeof value === "string") return true;
      return value.type === "application/pdf";
    }),
});

export const CreateOrganizervalidationSchema = Yup.object({
  ownerName: Yup.string()
    .trim()
    .matches(/^[A-Za-z ]+$/, "Only letters  are allowed")
    .max(20, "Max 20 characters allowed")
    .test(
      "no-leading-trailing-spaces",
      "Leading and trailing spaces are not allowed",
      (value) => value === value?.trim(),
    )
    .required("Owner name is required"),

  businessName: Yup.string()
    .matches(/^[A-Za-z ]+$/, "Only alphabets allowed")
    .max(30, "Max 30 characters allowed")
    .required("Business Name is required")
    .test(
      "no-leading-trailing-spaces",
      "Leading and trailing spaces are not allowed",
      (value) => value === value?.trim(),
    )
    .test(
      "not-only-spaces",
      "Business Name cannot be empty or only spaces",
      (value) => value && value.trim().length > 0,
    ),

  email: Yup.string()
    .trim()
    .matches(
      /^[a-zA-Z0-9._%+-]+@[a-zA-Z0-9.-]+\.[A-Za-z]{2,6}$/,
      "Enter a valid email address",
    )
    .required("Email is required"),
  phone: Yup.string()
    .required("Phone is required")
    .matches(/^\d+$/, "Phone must contain only numbers")
    .min(8, "Phone number is too short")
    .max(15, "Phone number cannot exceed 15 digits"),

  organizerType: Yup.array()
    .of(Yup.string())
    .min(1, "Please select at least one organizer type")
    .max(3, "You can select a maximum of 3 organizer types")
    .required("Organizer Type is required"),

  businessDescription: Yup.string()
    .required("Business description is required")
    .test(
      "not-empty",
      "Business description cannot be only spaces",
      (value) => value && value.trim().length > 0,
    )
    .min(10, "Business description must be at least 10 characters long")
    .max(100, "Business description cannot exceed 100 characters")
    .test(
      "no-leading-trailing-spaces",
      "Leading and trailing spaces are not allowed",
      (value) => value === value?.trim(),
    ),

  websiteUrl: Yup.string()
    .url("Enter a valid URL")
    .required("website Url is required"),

  country: Yup.string()
    .matches(/^[A-Za-z\s]+$/, "Only alphabets are allowed")
    .max(20, "Max 20 characters allowed")
    .required("Country is required")
    .test(
      "no-leading-trailing-spaces",
      "Spaces are not allowed",
      (value) => value && value.trim() === value,
    ),

  address: Yup.string()
    .max(50, "Max 50 characters allowed")
    .required("Address is required")
    .test(
      "no-leading-trailing-spaces",
      "Spaces are not allowed",
      (value) => value && value.trim() === value,
    ),

  city: Yup.string()
    .matches(/^[A-Za-z\s]+$/, "Only alphabets are allowed")
    .max(20, "Max 20 characters allowed")
    .required("City is required")
    .test(
      "no-leading-trailing-spaces",
      "Spaces are not allowed",
      (value) => value && value.trim() === value,
    ),

  state: Yup.string()
    .matches(/^[A-Za-z\s]+$/, "Only alphabets are allowed")
    .max(20, "Max 20 characters allowed")
    .required("State is required")
    .test(
      "no-leading-trailing-spaces",
      "Spaces are not allowed",
      (value) => value && value.trim() === value,
    ),

  facebook: Yup.string().url("Enter a valid URL").nullable().notRequired(),
  instagram: Yup.string().url("Enter a valid URL").nullable().notRequired(),
  linkedin: Yup.string().url("Enter a valid URL").nullable().notRequired(),
  twitter: Yup.string().url("Enter a valid URL").nullable().notRequired(),

  businessLogo: Yup.mixed()
    .required("Business logo is required")
    .test("fileType", "Only JPG, JPEG, or PNG files are allowed", (value) => {
      if (!value) return false;
      const allowedTypes = ["image/jpeg", "image/jpg", "image/png"];
      return allowedTypes.includes(value.type);
    }),

  coverPhoto: Yup.mixed()
    .required("Cover photo is required")
    .test("fileType", "Only JPG, JPEG, or PNG files are allowed", (value) => {
      if (!value) return false;
      const allowedTypes = ["image/jpeg", "image/jpg", "image/png"];
      return allowedTypes.includes(value.type);
    }),

  businessLicense: Yup.mixed()
    .required("Business license is required")
    .test("fileType", "Only PDF files are allowed", (value) => {
      if (!value) return false;
      return value.type === "application/pdf";
    }),
});

export const EventFieldsValidationSchemaforAdmin = Yup.object({
  organizer: Yup.string().required("Organizer is required"),

  title: Yup.string()
    .min(3, "Title must be at least 3 characters")
    .max(20, "Title must be at most 20 characters")
    .required("Title is required")
    .test(
      "not-only-spaces",
      "Title cannot be empty or only spaces",
      (value) => value && value.trim().length > 0,
    )
    .test("no-leading-trailing-spaces", "Spaces are not allowed", (value) => {
      if (!value) return true;
      return value === value.trim();
    }),

  ticketPrice: Yup.number()
    .typeError("Ticket Price must be a valid number")
    .min(1, "Ticket Price cannot be less than 1")
    .max(10000, "Ticket Price cannot be more than 10000")
    .required("Ticket Price is required"),

  description: Yup.string()
    .required("Description is required")
    .trim("Leading and trailing spaces are not allowed")
    .test(
      "not-only-spaces",
      "Description cannot contain only spaces",
      (value) => value && value.trim().length > 0,
    )
    .min(10, "Description must be at least 10 characters long")
    .max(200, "Description cannot exceed 200 characters")
    .test(
      "no-leading-trailing-spaces",
      "Leading and trailing spaces are not allowed",
      (value) => value === value?.trim(),
    ),

  eventType: Yup.string()
    .oneOf(
      [
        "playdate",
        "adoption_drive",
        "breed_meetup",
        "training_workshop",
        "cafe_pet_day",
        "other",
      ],
      "Invalid Event Type",
    )
    .required("Event Type is required"),

  startDate: Yup.date()
    .nullable()
    .required("Start date is required")
    .test("is-valid", "Please select a valid date", (value) => {
      if (!value) return false;
      return dayjs(value).isValid();
    })
    .test("not-past", "Start date cannot be in the past", (value) => {
      if (!value) return true;
      const today = dayjs().startOf("day");
      const selectedDate = dayjs(value).startOf("day");
      return selectedDate.isAfter(today) || selectedDate.isSame(today);
    }),

  // Start Time Validation
  startTime: Yup.string()
    .required("Start time is required")
    .matches(/^([0-1]?[0-9]|2[0-3]):[0-5][0-9]$/, "Please select a valid time")
    .test(
      "not-past-time",
      "Start time cannot be in the past",
      function (value) {
        const { startDate } = this.parent;

        if (!startDate || !value) return true;

        const today = dayjs().startOf("day");
        const selectedDate = dayjs(startDate).startOf("day");

        // Only check if date is today
        if (selectedDate.isSame(today)) {
          const now = dayjs();
          const selectedDateTime = dayjs(
            `${dayjs(startDate).format("YYYY-MM-DD")} ${value}`,
          );

          if (selectedDateTime.isBefore(now)) {
            return this.createError({
              message: "Start time cannot be in the past for today's date",
            });
          }
        }

        return true;
      },
    ),

  // End Date Validation
  endDate: Yup.date()
    .nullable()
    .required("End date is required")
    .test("is-valid", "Please select a valid date", (value) => {
      if (!value) return false;
      return dayjs(value).isValid();
    })
    .test(
      "is-after-start",
      "End date must be on or after start date",
      function (value) {
        const { startDate } = this.parent;
        if (!startDate || !value) return true;

        const start = dayjs(startDate).startOf("day");
        const end = dayjs(value).startOf("day");

        if (end.isBefore(start)) {
          return this.createError({
            message: "End date cannot be before start date",
          });
        }

        return true;
      },
    ),

  // End Time Validation
  endTime: Yup.string()
    .required("End time is required")
    .matches(/^([0-1]?[0-9]|2[0-3]):[0-5][0-9]$/, "Please select a valid time")
    .test(
      "is-after-start-time",
      "End time must be after start time",
      function (value) {
        const { startDate, startTime, endDate } = this.parent;

        if (!startDate || !startTime || !endDate || !value) return true;

        // Only validate if same date
        const startDay = dayjs(startDate).startOf("day");
        const endDay = dayjs(endDate).startOf("day");

        if (startDay.isSame(endDay)) {
          const startDateTime = dayjs(
            `${dayjs(startDate).format("YYYY-MM-DD")} ${startTime}`,
          );
          const endDateTime = dayjs(
            `${dayjs(endDate).format("YYYY-MM-DD")} ${value}`,
          );

          if (endDateTime.isBefore(startDateTime)) {
            return this.createError({
              message: "End time must be after start time on the same day",
            });
          }

          if (endDateTime.isSame(startDateTime)) {
            return this.createError({
              message: "End time cannot be the same as start time",
            });
          }
        }

        return true;
      },
    ),

  venueAddress: Yup.string()
    .min(5, "Venue Address must be at least 5 characters long")
    .max(150, "Venue Address cannot exceed 150 characters")
    .required("Venue Address is required")
    .test(
      "not-only-spaces",
      "Venue Address cannot be empty or only spaces",
      (value) => value && value.trim().length > 0,
    )
    .test(
      "no-leading-trailing-spaces",
      "Leading and trailing spaces are not allowed",
      (value) => {
        if (!value) return true; // Let required() handle empty
        return value === value.trim();
      },
    ),

  maxAttendees: Yup.number()
    .min(1, "Max attendee must be at least 1")
    .max(1000, "Max attendee cannot be more than 1000")
    .required("Attendees is required"),

  externalTicketLink: Yup.string()
    .url("Enter a valid URL")
    .required("External ticket link is required"),

  termsRestrictions: Yup.string()
    .required("Terms & Restrictions are required")
    .trim("Leading and trailing spaces are not allowed")
    .test(
      "not-only-spaces",
      "Terms & Restrictions cannot contain only spaces",
      (value) => value && value.trim().length > 0,
    )
    .min(10, "Must be at least 10 characters long")
    .max(100, "Must be at most 100 characters long")
    .test(
      "no-leading-trailing-spaces",
      "Leading and trailing spaces are not allowed",
      (value) => value === value?.trim(),
    ),

  coverImage: Yup.mixed()
    .required("Cover image is required")
    .test(
      "fileType",
      "Only JPG, JPEG, PNG, or SVG files are allowed",
      (value) => {
        if (!value) return true; // ✅ Changed from false to true - let required() handle empty
        const allowedTypes = [
          "image/jpeg",
          "image/png",
          "image/jpg",
          "image/svg+xml",
        ];
        return value instanceof File && allowedTypes.includes(value.type);
      },
    )
    .test("fileSize", "File size must be less than 5MB", (value) => {
      if (!value) return true; // ✅ Let required() handle empty
      return value instanceof File && value.size <= 5 * 1024 * 1024;
    }),

  galleryImages: Yup.array()
    .of(
      Yup.mixed()
        .test(
          "fileType",
          "Only JPG, JPEG, PNG, or SVG files are allowed",
          (file) => {
            if (!file) return true; // ✅ Changed from false to true
            const allowedTypes = [
              "image/jpeg",
              "image/png",
              "image/jpg",
              "image/svg+xml",
            ];
            return file instanceof File && allowedTypes.includes(file.type);
          },
        )
        .test("fileSize", "File size must be less than 5MB", (file) => {
          if (!file) return true;
          return file instanceof File && file.size <= 5 * 1024 * 1024;
        }),
    )
    .min(1, "At least one gallery image is required")
    .max(5, "Maximum 5 gallery images allowed")
    .required("Gallery images are required"),
});

export const CustomEditEventValidationSchema = Yup.object({
  title: Yup.string()
    .min(3, "Title must be at least 3 characters")
    .max(20, "Title must be at most 20 characters")
    .required("Title is required")
    .test(
      "not-only-spaces",
      "Title cannot be empty or only spaces",
      (value) => value && value.trim().length > 0,
    )
    .test("no-leading-trailing-spaces", "Spaces are not allowed", (value) => {
      if (!value) return true;
      return value === value.trim();
    }),

  ticketPrice: Yup.number()
    .typeError("Ticket Price must be a valid number")
    .min(1, "Ticket Price cannot be less than 1")
    .max(10000, "Ticket Price cannot be more than 10000")
    .required("Ticket Price is required"),

  description: Yup.string()
    .required("Description is required")
    .trim("Leading and trailing spaces are not allowed")
    .test(
      "not-only-spaces",
      "Description cannot contain only spaces",
      (value) => value && value.trim().length > 0,
    )
    .min(10, "Description must be at least 10 characters long")
    .max(200, "Description cannot exceed 200 characters"),

  eventType: Yup.string()
    .oneOf(
      [
        "playdate",
        "adoption_drive",
        "breed_meetup",
        "training_workshop",
        "cafe_pet_day",
        "other",
      ],
      "Invalid Event Type",
    )
    .required("Event Type is required"),

  startDate: Yup.date()
    .nullable()
    .required("Start date is required")
    .test("is-valid", "Please select a valid date", (value) => {
      if (!value) return false;
      return dayjs(value).isValid();
    }),

  startTime: Yup.string()
    .required("Start time is required")
    .matches(/^([0-1]?[0-9]|2[0-3]):[0-5][0-9]$/, "Please select a valid time"),

  endDate: Yup.date()
    .nullable()
    .required("End date is required")
    .test("is-valid", "Please select a valid date", (value) => {
      if (!value) return false;
      return dayjs(value).isValid();
    })
    .test(
      "is-after-start",
      "End date must be on or after start date",
      function (value) {
        const { startDate } = this.parent;
        if (!startDate || !value) return true;
        const start = dayjs(startDate).startOf("day");
        const end = dayjs(value).startOf("day");
        return !end.isBefore(start);
      },
    ),

  endTime: Yup.string()
    .required("End time is required")
    .matches(/^([0-1]?[0-9]|2[0-3]):[0-5][0-9]$/, "Please select a valid time")
    .test(
      "is-after-start-time",
      "End time must be after start time",
      function (value) {
        const { startDate, startTime, endDate } = this.parent;
        if (!startDate || !startTime || !endDate || !value) return true;

        const startDay = dayjs(startDate).startOf("day");
        const endDay = dayjs(endDate).startOf("day");

        if (startDay.isSame(endDay)) {
          const startDateTime = dayjs(
            `${dayjs(startDate).format("YYYY-MM-DD")} ${startTime}`,
          );
          const endDateTime = dayjs(
            `${dayjs(endDate).format("YYYY-MM-DD")} ${value}`,
          );
          return endDateTime.isAfter(startDateTime);
        }

        return true;
      },
    ),

  venueAddress: Yup.string()
    .min(5, "Venue Address must be at least 5 characters long")
    .max(150, "Venue Address cannot exceed 150 characters")
    .required("Venue Address is required")
    .test(
      "not-only-spaces",
      "Venue Address cannot be empty or only spaces",
      (value) => value && value.trim().length > 0,
    )
    .test(
      "no-leading-trailing-spaces",
      "Leading and trailing spaces are not allowed",
      (value) => {
        if (!value) return true;
        return value === value.trim();
      },
    ),

  coordinates: Yup.object()
    .shape({
      latitude: Yup.number()
        .typeError("Latitude must be a valid number")
        .required("Latitude is required")
        .min(-90, "Latitude must be between -90 and 90")
        .max(90, "Latitude must be between -90 and 90"),
      longitude: Yup.number()
        .typeError("Longitude must be a valid number")
        .required("Longitude is required")
        .min(-180, "Longitude must be between -180 and 180")
        .max(180, "Longitude must be between -180 and 180"),
    })
    .required("Coordinates are required"),

  maxAttendees: Yup.number()
    .min(1, "Max attendee must be at least 1")
    .max(1000, "Max attendee cannot be more than 1000")
    .required("Attendees is required"),

  externalTicketLink: Yup.string()
    .url("Enter a valid URL")
    .required("External Ticket Link is required"),

  termsRestrictions: Yup.string()
    .required("Terms & Restrictions are required")
    .trim("Leading and trailing spaces are not allowed")
    .test(
      "not-only-spaces",
      "Terms & Restrictions cannot contain only spaces",
      (value) => value && value.trim().length > 0,
    )
    .min(10, "Must be at least 10 characters long")
    .max(100, "Must be at most 100 characters long"),

  // ✅ FIXED VALIDATION FOR IMAGES
  coverImage: Yup.mixed()
    .nullable()
    .test("cover-required", "Cover image is required", function (value) {
      const { existingCoverImage } = this.parent;
      // must have either new or existing cover image
      return Boolean(value || existingCoverImage);
    }),

  galleryImages: Yup.array()
    .of(Yup.mixed())
    .test(
      "gallery-required",
      "At least one gallery image is required",
      function (value) {
        const { existingGalleryImages } = this.parent;
        const total =
          (value?.length || 0) + (existingGalleryImages?.length || 0);
        return total > 0;
      },
    )
    .test("max-gallery", "Maximum 5 gallery images allowed", function (value) {
      const { existingGalleryImages } = this.parent;
      const total = (value?.length || 0) + (existingGalleryImages?.length || 0);
      return total <= 5;
    }),

  existingCoverImage: Yup.mixed().nullable(),
  existingGalleryImages: Yup.array().of(Yup.mixed()),
});

export const EditVendorSchema = Yup.object().shape({
  logo: Yup.mixed()
    .nullable()
    .notRequired()
    .test("required", "Logo is required", function (value) {
      const { existingLogo } = this.parent;
      return !!(value instanceof File || existingLogo);
    })
    .test("fileType", "Only JPEG, PNG, JPG images are allowed", (value) => {
      if (!value) return true;
      if (value instanceof File) {
        return ["image/jpeg", "image/png", "image/jpg"].includes(value.type);
      }
      return true;
    }),

  // coverPhoto: Yup.mixed()
  //   .nullable()
  //   .notRequired()
  //   .test("required", "Cover photo is required", function (value) {
  //     const { existingCoverPhoto } = this.parent;
  //     return !!(value instanceof File || existingCoverPhoto);
  //   })
  //   .test("fileType", "Only JPEG, PNG, JPG images are allowed", (value) => {
  //     if (!value) return true;
  //     if (value instanceof File) {
  //       return ["image/jpeg", "image/png", "image/jpg"].includes(value.type);
  //     }
  //     return true;
  //   }),

  // businessimage: Yup.mixed()
  //   .nullable()
  //   .notRequired()
  //   .test("required", "Business image is required", function (value) {
  //     const { existingBusinessImage } = this.parent;
  //     return !!(value instanceof File || existingBusinessImage);
  //   })
  //   .test("fileType", "Only JPEG, PNG, JPG images are allowed", (value) => {
  //     if (!value) return true;
  //     if (value instanceof File) {
  //       return ["image/jpeg", "image/png", "image/jpg"].includes(value.type);
  //     }
  //     return true;
  //   }),

  // galleryImages: Yup.array()
  //   .nullable()
  //   .notRequired()
  //   .test(
  //     "required",
  //     "At least one gallery image is required",
  //     function (value) {
  //       const { existingGalleryImages } = this.parent;
  //       const totalCount =
  //         (value?.length || 0) + (existingGalleryImages?.length || 0);
  //       return totalCount > 0;
  //     },
  //   )
  //   .max(5, "Maximum 5 gallery images allowed")
  //   .test("fileType", "Only JPEG, PNG, JPG images are allowed", (values) => {
  //     if (!values || values.length === 0) return true;
  //     return values.every((file) => {
  //       if (file instanceof File) {
  //         return ["image/jpeg", "image/png", "image/jpg"].includes(file.type);
  //       }
  //       return true;
  //     });
  //   }),

  businessName: Yup.string()
    .trim("Business Name cannot be empty or spaces only")
    .strict(true)
    .max(30, "Business Name must be at most 30 characters")
    .required("Business Name is required")
    .test(
      "not-only-spaces",
      "Business Name cannot be empty or spaces only",
      (value) => value && value.trim().length > 0,
    ),

  ownerName: Yup.string()
    .trim("Owner Name cannot be empty or spaces only")
    .strict(true)
    .max(30, "Owner Name must be at most 30 characters")
    .required("Owner Name is required")
    .test(
      "not-only-spaces",
      "Owner Name cannot be empty or spaces only",
      (value) => value && value.trim().length > 0,
    ),

  email: Yup.string()
    .email("Invalid email address")
    .required("Email is required"),

  phone: Yup.string()
    .required("Phone is required")
    .matches(/^\d+$/, "Phone must contain only numbers")
    .min(8, "Phone number is too short")
    .max(15, "Phone number cannot exceed 15 digits"),

  description: Yup.string()
    .min(10, "Description must be at least 10 characters")
    .max(60, "Description must be at most 60 characters")
    .required("Description is required")
    .test(
      "not-only-spaces",
      "Description cannot be empty or spaces only",
      (value) => value && value.trim().length > 0,
    ),

  // businessType: Yup.array()
  //   .of(Yup.string())
  //   .min(1, "Select at least one business type")
  //   .required("Business Type is required"),

  // Address
  // street: Yup.string()
  //   .trim()
  //   .max(20, "Street must be at most 20 characters")
  //   .required("Street is required"),

  // city: Yup.string()
  //   .trim()
  //   .matches(/^[A-Za-z\s]+$/, "Only alphabets are allowed")
  //   .max(20, "City must be at most 20 characters")
  //   .required("City is required"),

  // state: Yup.string()
  //   .trim()
  //   .matches(/^[A-Za-z\s]+$/, "Only alphabets are allowed")
  //   .max(20, "State must be at most 20 characters")
  //   .required("State is required"),

  // country: Yup.string()
  //   .trim()
  //   .matches(/^[A-Za-z\s]+$/, "Only alphabets are allowed")
  //   .max(20, "Country must be at most 20 characters")
  //   .required("Country is required"),

  // lat: Yup.number()
  //   .typeError("Latitude must be a number")
  //   .min(-90, "Latitude must be between -90 and 90")
  //   .max(90, "Latitude must be between -90 and 90")
  //   .required("Latitude is required"),

  // lng: Yup.number()
  //   .typeError("Longitude must be a number")
  //   .min(-180, "Longitude must be between -180 and 180")
  //   .max(180, "Longitude must be between -180 and 180")
  //   .required("Longitude is required"),

  // Social Media
  facebook: Yup.string()
    .nullable()
    .test(
      "url",
      "Invalid Facebook URL",
      (value) => !value || Yup.string().url().isValidSync(value),
    ),

  instagram: Yup.string()
    .nullable()
    .test(
      "url",
      "Invalid Instagram URL",
      (value) => !value || Yup.string().url().isValidSync(value),
    ),

  tiktok: Yup.string()
    .nullable()
    .test(
      "url",
      "Invalid TikTok URL",
      (value) => !value || Yup.string().url().isValidSync(value),
    ),

  website: Yup.string()
    .nullable()
    .test(
      "url",
      "Invalid Website URL",
      (value) => !value || Yup.string().url().isValidSync(value),
    ),

  // Operating Hours Validation
  // ...[
  //   "monday",
  //   "tuesday",
  //   "wednesday",
  //   "thursday",
  //   "friday",
  //   "saturday",
  //   "sunday",
  // ].reduce((acc, day) => {
  //   const dayCapitalized = day.charAt(0).toUpperCase() + day.slice(1);

  //   // Open Time Validation
  //   acc[`${day}Open`] = Yup.string().when(`${day}Closed`, {
  //     is: false,
  //     then: (schema) =>
  //       schema
  //         .required(`${dayCapitalized} opening time is required`)
  //         .matches(
  //           /^([0-1]?[0-9]|2[0-3]):[0-5][0-9]$/,
  //           "Please select a valid time",
  //         ),
  //     otherwise: (schema) => schema.nullable(),
  //   });

  //   // Close Time Validation
  //   acc[`${day}Close`] = Yup.string()
  //     .when(`${day}Closed`, {
  //       is: false,
  //       then: (schema) =>
  //         schema
  //           .required(`${dayCapitalized} closing time is required`)
  //           .matches(
  //             /^([0-1]?[0-9]|2[0-3]):[0-5][0-9]$/,
  //             "Please select a valid time",
  //           ),
  //       otherwise: (schema) => schema.nullable(),
  //     })
  //     .test(
  //       "is-after-open",
  //       "Close time must be after open time",
  //       function (value) {
  //         const openTime = this.parent[`${day}Open`];
  //         const isClosed = this.parent[`${day}Closed`];

  //         if (isClosed || !openTime || !value) return true;

  //         const openDateTime = dayjs(openTime, "HH:mm");
  //         const closeDateTime = dayjs(value, "HH:mm");

  //         if (closeDateTime.isBefore(openDateTime)) {
  //           return this.createError({
  //             message: "Close time must be after open time",
  //           });
  //         }

  //         if (closeDateTime.isSame(openDateTime)) {
  //           return this.createError({
  //             message: "Close time cannot be the same as open time",
  //           });
  //         }

  //         return true;
  //       },
  //     );

  //   // Closed Checkbox
  //   acc[`${day}Closed`] = Yup.boolean();

  //   return acc;
  // }, {}),
});

export const AddVendorSchema = Yup.object().shape({
  logo: Yup.mixed()
    .required("Logo is required")
    .test("fileType", "Only JPEG, PNG, JPG images are allowed", (value) => {
      if (!value) return false;
      if (value instanceof File) {
        return ["image/jpeg", "image/png", "image/jpg"].includes(value.type);
      }
      return true;
    }),

  businessName: Yup.string()
    .trim("Business Name cannot be empty or spaces only")
    .strict(true)
    .max(30, "Business Name must be at most 30 characters")
    .required("Business Name is required")
    .test(
      "not-only-spaces",
      "Business Name cannot be empty or spaces only",
      (value) => value && value.trim().length > 0,
    ),

  ownerName: Yup.string()
    .trim("Owner Name cannot be empty or spaces only")
    .strict(true)
    .max(30, "Owner Name must be at most 30 characters")
    .required("Owner Name is required")
    .test(
      "not-only-spaces",
      "Owner Name cannot be empty or spaces only",
      (value) => value && value.trim().length > 0,
    ),

  email: Yup.string()
    .email("Invalid email address")
    .required("Email is required"),

  phone: Yup.string()
    .required("Phone is required")
    .matches(/^\d+$/, "Phone must contain only numbers")
    .min(8, "Phone number is too short")
    .max(15, "Phone number cannot exceed 15 digits"),

  description: Yup.string()
    .min(10, "Description must be at least 10 characters")
    .max(60, "Description must be at most 60 characters")
    .required("Description is required")
    .test(
      "not-only-spaces",
      "Description cannot be empty or spaces only",
      (value) => value && value.trim().length > 0,
    ),

  // businessType: Yup.array()
  //   .of(Yup.string())
  //   .min(1, "Select at least one business type")
  //   .required("Business Type is required"),
  // Address
  // street: Yup.string()
  //   .trim()
  //   .max(20, "Street must be at most 20 characters")
  //   .required("Street is required"),

  // city: Yup.string()
  //   .trim()
  //   .matches(/^[A-Za-z\s]+$/, "Only alphabets are allowed")
  //   .max(20, "City must be at most 20 characters")
  //   .required("City is required"),

  // state: Yup.string()
  //   .trim()
  //   .matches(/^[A-Za-z\s]+$/, "Only alphabets are allowed")
  //   .max(20, "State must be at most 20 characters")
  //   .required("State is required"),

  // country: Yup.string()
  //   .trim()
  //   .matches(/^[A-Za-z\s]+$/, "Only alphabets are allowed")
  //   .max(20, "Country must be at most 20 characters")
  //   .required("Country is required"),

  // lat: Yup.number()
  //   .typeError("Latitude must be a number")
  //   .min(-90, "Latitude must be between -90 and 90")
  //   .max(90, "Latitude must be between -90 and 90")
  //   .required("Latitude is required"),

  // lng: Yup.number()
  //   .typeError("Longitude must be a number")
  //   .min(-180, "Longitude must be between -180 and 180")
  //   .max(180, "Longitude must be between -180 and 180")
  //   .required("Longitude is required"),

  facebook: Yup.string()
    .nullable()
    .test(
      "url",
      "Invalid Facebook URL",
      (value) => !value || Yup.string().url().isValidSync(value),
    ),

  instagram: Yup.string()
    .nullable()
    .test(
      "url",
      "Invalid Instagram URL",
      (value) => !value || Yup.string().url().isValidSync(value),
    ),

  tiktok: Yup.string()
    .nullable()
    .test(
      "url",
      "Invalid TikTok URL",
      (value) => !value || Yup.string().url().isValidSync(value),
    ),

  website: Yup.string()
    .nullable()
    .test(
      "url",
      "Invalid Website URL",
      (value) => !value || Yup.string().url().isValidSync(value),
    ),

  // Operating Hours Validation
  // ...[
  //   "monday",
  //   "tuesday",
  //   "wednesday",
  //   "thursday",
  //   "friday",
  //   "saturday",
  //   "sunday",
  // ].reduce((acc, day) => {
  //   const dayCapitalized = day.charAt(0).toUpperCase() + day.slice(1);

  //   // Open Time Validation
  //   acc[`${day}Open`] = Yup.string().when(`${day}Closed`, {
  //     is: false,
  //     then: (schema) =>
  //       schema
  //         .required(`${dayCapitalized} opening time is required`)
  //         .matches(
  //           /^([0-1]?[0-9]|2[0-3]):[0-5][0-9]$/,
  //           "Please select a valid time",
  //         ),
  //     otherwise: (schema) => schema.nullable(),
  //   });

  //   // Close Time Validation
  //   acc[`${day}Close`] = Yup.string()
  //     .when(`${day}Closed`, {
  //       is: false,
  //       then: (schema) =>
  //         schema
  //           .required(`${dayCapitalized} closing time is required`)
  //           .matches(
  //             /^([0-1]?[0-9]|2[0-3]):[0-5][0-9]$/,
  //             "Please select a valid time",
  //           ),
  //       otherwise: (schema) => schema.nullable(),
  //     })
  //     .test(
  //       "is-after-open",
  //       "Close time must be after open time",
  //       function (value) {
  //         const openTime = this.parent[`${day}Open`];
  //         const isClosed = this.parent[`${day}Closed`];

  //         if (isClosed || !openTime || !value) return true;

  //         const openDateTime = dayjs(openTime, "HH:mm");
  //         const closeDateTime = dayjs(value, "HH:mm");

  //         if (closeDateTime.isBefore(openDateTime)) {
  //           return this.createError({
  //             message: "Close time must be after open time",
  //           });
  //         }

  //         if (closeDateTime.isSame(openDateTime)) {
  //           return this.createError({
  //             message: "Close time cannot be the same as open time",
  //           });
  //         }

  //         return true;
  //       },
  //     );

  //   // Closed Checkbox
  //   acc[`${day}Closed`] = Yup.boolean();

  //   return acc;
  // }, {}),
});

export const AddFAQSchema = Yup.object().shape({
  question: Yup.string()
    .required("Question is required")
    .test("not-only-spaces", "Question cannot be only spaces", (value) => {
      if (!value) return false;
      return value.trim().length > 0;
    })
    .test("min-length", "Question must be at least 10 characters", (value) => {
      if (!value) return false;
      return value.trim().length >= 10;
    })
    .test("max-length", "Question must not exceed 80 characters", (value) => {
      if (!value) return true;
      return value.trim().length <= 80;
    }),
  answer: Yup.string()
    .required("Answer is required")
    .test("not-only-spaces", "Answer cannot be only spaces", (value) => {
      if (!value) return false; // Required check handles empty
      // Strip HTML tags and convert HTML entities to spaces
      const plainText = value
        .replace(/<\/?[^>]*>/g, "")
        .replace(/&nbsp;/g, " ")
        .replace(/<br\s*[/]?>/gi, "\n")
        .trim();
      return plainText.length > 0;
    })
    .test("min-length", "Answer must be at least 10 characters", (value) => {
      if (!value) return false;
      // Strip HTML tags and convert HTML entities to spaces for length check
      const plainText = value
        .replace(/<\/?[^>]*>/g, "")
        .replace(/&nbsp;/g, " ")
        .replace(/<br\s*[/]?>/gi, "\n")
        .trim();
      return plainText.length >= 10;
    })
    .max(200, "Answer must not exceed 200 characters")
    .trim(),
  category: Yup.string()
    .required("Category is required")
    .oneOf(
      [
        "Account & Login Issues",
        "Matches & Messaging",
        "Subscription & Payments",
        "Pet Features",
        "Technical Issues",
        "Events & Bookings",
        "Vendor Services",
        "General",
      ],
      "Please select a valid category",
    ),
});

export const AddBannerSchema = Yup.object().shape({
  title: Yup.string()
    .min(3, "Title must be at least 3 characters")
    .max(20, "Title must be at most 20 characters")
    .required("Title is required")
    .test(
      "not-only-spaces",
      "Title cannot be empty or only spaces",
      (value) => value && value.trim().length > 0,
    )
    .matches(
      /^(?!\d+$)[A-Za-z0-9\s]+$/,
      "Title cannot contain only numbers or special characters",
    ),

  description: Yup.string()
    .min(10, "Description must be at least 10 characters")
    .max(80, "Description must be at most 80 characters")
    .required("Description is required")
    .test(
      "not-only-spaces",
      "Description cannot be empty or only spaces",
      (value) => value && value.trim().length > 0,
    ),

  placement: Yup.string()
    .oneOf(["homepage", "event", "dashboard"], "Invalid placement")
    .required("Placement is required"),

  link: Yup.string()
    .matches(
      /^(https?:\/\/|www\.)[^\s/$.?#].[^\s]*$/i,
      "Must be a valid URL (http, https, or www)",
    )
    .required("Link is required"),

  startDate: Yup.date()

    .required("Start Date is required")
    .test("is-valid", "Please select a valid date", (value) =>
      value ? dayjs(value).isValid() : false,
    )
    .test("not-past", "Start date cannot be in the past", (value) =>
      value ? dayjs(value).isAfter(dayjs().subtract(1, "day")) : false,
    ),

  endDate: Yup.date()

    .required("End Date is required")
    .test("is-valid", "Please select a valid date", (value) =>
      value ? dayjs(value).isValid() : false,
    )
    .test("is-after", "End Date must be after Start Date", function (value) {
      const { startDate } = this.parent;
      if (!startDate || !value) return true;
      return dayjs(value).isAfter(dayjs(startDate));
    }),

  image: Yup.mixed()
    .required("Banner image is required")
    .test(
      "fileType",
      "Only JPG, JPEG, PNG image formats are allowed",
      (value) => {
        if (!value) return false; // Let required() handle empty values

        // Allow existing image URLs (string) or new File uploads
        if (typeof value === "string") return true;

        // Check if it's a File and has correct type
        if (value instanceof File) {
          const allowedTypes = [
            "image/jpeg",
            "image/jpg",
            "image/png",
            "image/gif",
            "image/webp",
            "image/svg+xml",
          ];
          return allowedTypes.includes(value.type);
        }
        return false;
      },
    )
    .test("fileSize", "Image size must be less than 5MB", (value) => {
      if (!value) return true; // Let required() handle empty values
      if (typeof value === "string") return true; // Skip for URLs

      if (value instanceof File) {
        return value.size <= 5 * 1024 * 1024; // 5MB
      }
      return false;
    }),
});

export const PetsitterSchema = Yup.object().shape({
  profilePhoto: Yup.mixed().required("Profile photo is required"),
  coverPhoto: Yup.mixed().required("Cover photo is required"),
  businessName: Yup.string().required("Business name is required"),
  ownerName: Yup.string().required("Owner name is required"),
  email: Yup.string().email("Invalid email").required("Email is required"),
  phone: Yup.string().required("Phone is required"),
  description: Yup.string().required("Description is required"),
  services: Yup.array().min(1, "At least one service is required"),
  experience: Yup.number().required("Experience is required").min(0),
  hourlyRate: Yup.number().required("Hourly rate is required").min(0),
  street: Yup.string().required("Street is required"),
  city: Yup.string().required("City is required"),
  state: Yup.string().required("State is required"),
  country: Yup.string().required("Country is required"),
  lat: Yup.number().required("Latitude is required"),
  lng: Yup.number().required("Longitude is required"),

  galleryImages: Yup.array().min(1, "At least one gallery image is required"),
});

export const PetSpotSchema = Yup.object().shape({
  title: Yup.string()
    .required("Title is required")
    .min(3, "Title must be at least 3 characters")
    .max(50, "Title must be at most 50 characters")
    .test(
      "not-only-spaces",
      "Title cannot be empty or only spaces",
      (value) => value && value.trim().length > 0,
    ),
  description: Yup.string()
    .required("Description is required")
    .min(10, "Description must be at least 10 characters")
    .max(500, "Description must be at most 500 characters")
    .test(
      "not-only-spaces",
      "Description cannot be empty or only spaces",
      (value) => value && value.trim().length > 0,
    ),
  openTime: Yup.string()
    .required("Open time is required")
    .matches(
      /^([0-1]?[0-9]|2[0-3]):[0-5][0-9]\s(AM|PM)$/i,
      "Please enter time in format HH:MM AM/PM",
    ),
  closeTime: Yup.string()
    .required("Close time is required")
    .matches(
      /^([0-1]?[0-9]|2[0-3]):[0-5][0-9]\s(AM|PM)$/i,
      "Please enter time in format HH:MM AM/PM",
    )
    .test(
      "is-after-open",
      "Close time must be after open time",
      function (value) {
        const { openTime } = this.parent;
        if (!openTime || !value) return true; // Let required handle empty values

        // Convert time strings to 24-hour format for comparison
        const convertTo24Hour = (timeStr) => {
          const [time, period] = timeStr.trim().split(/\s+/);
          const [hours, minutes] = time.split(":");
          let hour24 = parseInt(hours, 10);

          if (period.toUpperCase() === "PM" && hour24 !== 12) {
            hour24 += 12;
          } else if (period.toUpperCase() === "AM" && hour24 === 12) {
            hour24 = 0;
          }

          return hour24 * 60 + parseInt(minutes, 10); // Convert to minutes for easy comparison
        };

        try {
          const openMinutes = convertTo24Hour(openTime);
          const closeMinutes = convertTo24Hour(value);

          return closeMinutes > openMinutes;
        } catch (error) {
          return true; // If conversion fails, let format validation handle it
        }
      },
    ),
  street: Yup.string()
    .required("Street is required")
    .min(3, "Street must be at least 3 characters")
    .max(100, "Street must be at most 100 characters"),
  city: Yup.string()
    .required("City is required")
    .matches(/^[A-Za-z\s]+$/, "City can only contain alphabets")
    .max(50, "City must be at most 50 characters"),
  state: Yup.string()
    .required("State is required")
    .matches(/^[A-Za-z\s]+$/, "State can only contain alphabets")
    .max(50, "State must be at most 50 characters"),
  country: Yup.string()
    .required("Country is required")
    .matches(/^[A-Za-z\s]+$/, "Country can only contain alphabets")
    .max(50, "Country must be at most 50 characters"),
  latitude: Yup.number()
    .typeError("Latitude must be a valid number")
    .required("Latitude is required")
    .min(-90, "Latitude must be between -90 and 90")
    .max(90, "Latitude must be between -90 and 90"),
  longitude: Yup.number()
    .typeError("Longitude must be a valid number")
    .required("Longitude is required")
    .min(-180, "Longitude must be between -180 and 180")
    .max(180, "Longitude must be between -180 and 180"),
  price: Yup.number()
    .typeError("Price must be a valid number")
    .min(0, "Price cannot be negative")
    .when("costType", {
      is: "paid",
      then: (schema) => schema.required("Price is required for paid spots"),
      otherwise: (schema) => schema.nullable(),
    }),
  costType: Yup.string()
    .required("Cost type is required")
    .oneOf(["free", "paid"], "Cost type must be either free or paid"),
  allowedPetTypes: Yup.array()
    .of(Yup.string())
    .min(1, "At least one pet type is required")
    .required("Allowed pet types are required"),
  facilities: Yup.array()
    .of(Yup.string())
    .min(1, "At least one facility is required")
    .required("Facilities are required"),
  placeType: Yup.string()
    .required("Place type is required")
    .oneOf(
      ["park", "cafe", "beach", "restaurant", "hotel", "other"],
      "Invalid place type",
    ),
  image: Yup.mixed()
    .nullable()
    .test("fileType", "Only JPG, JPEG, PNG images are allowed", (value) => {
      if (!value || typeof value === "string") return true;
      return ["image/jpeg", "image/png", "image/jpg"].includes(value.type);
    }),
});

export const CategorySchema = Yup.object().shape({
  name: Yup.string()
    .trim()
    .required("Category name is required")
    .min(2, "Category name must be at least 2 characters")
    .test("not-only-spaces", "Category name is required", (value) => {
      return value && value.trim().length > 0;
    }),
  image: Yup.array()
    .default(() => [])
    .typeError("Image must be an array")
    .max(1, "Only one image is allowed")
    .test(
      "file-type",
      "Only PNG, JPG, JPEG, and WEBP formats are allowed",
      function (value) {
        if (!value || !Array.isArray(value) || value.length === 0) return true; // Allow empty array
        return value.every((item) => {
          if (typeof item === "string") return true; // Existing images from server or data URLs
          if (!item) return true; // Allow null/empty values
          return (
            item &&
            ["image/png", "image/jpg", "image/jpeg", "image/webp"].includes(
              item.type,
            )
          );
        });
      },
    ),
});

export const SubcategorySchema = Yup.object().shape({
  name: Yup.string()
    .trim()
    .required("Sub Category name is required")
    .min(2, "Sub Category name must be at least 2 characters")
    .test("not-only-spaces", "Sub Category name is required", (value) => {
      return value && value.trim().length > 0;
    }),
});

export const PushNotificationSchema = Yup.object().shape({
  title: Yup.string()
    .trim()
    .min(5, "Title must be at least 5 characters")
    .max(50, "Title must not exceed 50 characters")
    .required("Title is required"),

  message: Yup.string()
    .trim()
    .min(20, "Message must be at least 20 characters")
    .max(200, "Message must not exceed 200 characters")
    .required("Message is required"),

  campaignType: Yup.string().required("Campaign type is required"),

  notificationType: Yup.string().required("Notification type is required"),

  targetAudience: Yup.string().required("Target audience is required"),

  scheduleType: Yup.string().required("Schedule type is required"),

  scheduledAt: Yup.date()
    .nullable()
    .when("scheduleType", {
      is: "scheduled",
      then: (schema) =>
        schema
          .required("Scheduled date is required for scheduled campaigns")
          .min(new Date(), "Scheduled date must be in the future"),
      otherwise: (schema) => schema.nullable(),
    }),

  filters: Yup.object().when("targetAudience", {
    is: "specific_users",
    then: (schema) =>
      schema.shape({
        subscriptionStatus: Yup.string(),
        countries: Yup.array().of(Yup.string()),
        states: Yup.array().of(Yup.string()),
        cities: Yup.array().of(Yup.string()),
        interests: Yup.array().of(Yup.string()),
        lookingFor: Yup.array().of(Yup.string()),
        hasPet: Yup.string(),
      }),
    otherwise: (schema) => schema.nullable(),
  }),
});

export const EmailCampaignSchema = Yup.object().shape({
  title: Yup.string()
    .trim()
    .min(5, "Title must be at least 5 characters")
    .max(50, "Title must not exceed 50 characters")
    .required("Title is required"),

  campaignType: Yup.string().required("Campaign type is required"),

  notificationType: Yup.string().required("Campaign type is required"),

  targetAudience: Yup.string().required("Target audience is required"),

  emailTemplate: Yup.object().shape({
    subject: Yup.string()
      .trim()
      .min(5, "Email subject must be at least 5 characters")
      .max(100, "Email subject must not exceed 100 characters")
      .required("Email subject is required"),

    htmlContent: Yup.string()
      .test(
        "html-content-length",
        "Email content must be at least 10 characters",
        function (value) {
          if (!value) return false;
          const textContent = stripHtml(value);
          return textContent.trim().length >= 10;
        },
      )
      .test(
        "html-content-max-length",
        "Email content must not exceed 200 characters",
        function (value) {
          if (!value) return true;
          const textContent = stripHtml(value);
          return textContent.trim().length <= 200;
        },
      )
      .required("Email content is required"),
  }),

  scheduleType: Yup.string().required("Schedule type is required"),

  scheduledAt: Yup.date()
    .nullable()
    .when("scheduleType", {
      is: "scheduled",
      then: (schema) =>
        schema
          .required("Scheduled date and time is required")
          .min(new Date(), "Scheduled date must be in the future"),
      otherwise: (schema) => schema.nullable(),
    }),

  filters: Yup.object().when("targetAudience", {
    is: "specific_users",
    then: (schema) =>
      schema.shape({
        subscriptionStatus: Yup.string(),
        countries: Yup.array().of(Yup.string()),
        interests: Yup.array().of(Yup.string()),
      }),
    otherwise: (schema) => schema.nullable(),
  }),
});
